import Head from 'next/head'
import Link from 'next/link'
import type { ReactElement } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Building2, User, Eye, EyeOff, Shield, Briefcase, Sparkles, Heart } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { supabase } from '@/lib/supabaseClient'
import { WorkOSSSOButton } from '@/components/auth/workos-sso-button'
import { WorkOSOrganizationSelector } from '@/components/auth/workos-organization-selector'

export default function SignInHub(): ReactElement {
	const router = useRouter()
	
	// Individual state
	const [indEmail, setIndEmail] = useState<string>('')
	const [indPassword, setIndPassword] = useState<string>('')
	const [indError, setIndError] = useState<string>('')
	const [indLoading, setIndLoading] = useState<boolean>(false)
	const [indShowPassword, setIndShowPassword] = useState<boolean>(false)

	// Corporate state
	const [corpEmail, setCorpEmail] = useState<string>('')
	const [corpError, setCorpError] = useState<string>('')
	const [corpMessage, setCorpMessage] = useState<string>('')
	const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null)
	const [isSearching, setIsSearching] = useState<boolean>(false)

	const checkProfileAndRedirect = useCallback(async (token: string): Promise<void> => {
		try {
			const response = await fetch('/api/user-profile-status', {
				method: 'GET',
				cache: 'no-store',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			if (response.ok) {
				const data = await response.json()
				// Redirect to profile page if user hasn't completed their profile
				if (data.isFirstTime || !data.hasCompletedProfile) {
					router.push('/profile')
				} else {
					router.push('/dashboard')
				}
			} else {
				// If we can't check profile status, redirect to dashboard
				router.push('/dashboard')
			}
		} catch (error) {
			console.error('Error checking profile status:', error)
			// If there's an error, redirect to dashboard
			router.push('/dashboard')
		}
	}, [router])

	// Check for existing session and redirect if already authenticated
	useEffect((): (() => void) => {
		const run = async (): Promise<void> => {
			const { data } = await supabase.auth.getSession()
			if (data?.session) {
				await checkProfileAndRedirect(data.session.access_token)
			}
		}
		void run()
		const { data: listener } = supabase.auth.onAuthStateChange(async (_e, session) => {
			if (session) {
				await checkProfileAndRedirect(session.access_token)
			}
		})
		return () => listener.subscription.unsubscribe()
	}, [router, checkProfileAndRedirect])

	// Check for query parameters for messages and errors
	useEffect((): void => {
		const { error: queryError, message: queryMessage } = router.query
		if (queryError) {
			setCorpError(String(queryError).replace(/_/g, ' '))
		}
		if (queryMessage) {
			setCorpMessage(String(queryMessage).replace(/_/g, ' '))
		}
	}, [router.query])

	async function handleIndividualSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
		e.preventDefault()
		setIndLoading(true)
		setIndError('')
		
		const { data, error } = await supabase.auth.signInWithPassword({ email: indEmail, password: indPassword })
		if (error) {
			setIndError(error.message)
			setIndLoading(false)
			return
		}
		if (data?.session) {
			await checkProfileAndRedirect(data.session.access_token)
		}
	}

	const handleCorporateSearch = (): void => {
		if (corpEmail) {
			setIsSearching(true)
			setSelectedOrganization(null)
		}
	}

	const handleCorporateEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		setCorpEmail(e.target.value)
		// Reset search state when email changes
		if (isSearching) {
			setIsSearching(false)
			setSelectedOrganization(null)
		}
	}

	return (
		<>
			<Head>
				<title>Sign In – KEM Discovery</title>
				<meta name="description" content="Choose how you want to sign in – individual or corporate portal." />
			</Head>

			<div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12">
				<div className="w-full max-w-5xl">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Individual Access */}
						<Card className="border-0 shadow-xl bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-900 dark:to-purple-950/30">
							<CardHeader className="text-center pb-4">
								<div className="mx-auto size-16 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white grid place-items-center shadow-lg mb-4">
									<User className="size-8" />
								</div>
								<CardTitle className="text-2xl font-bold">Personal Discovery</CardTitle>
								<p className="text-sm text-muted-foreground mt-2">Begin your personalized journey of emotional understanding</p>
							</CardHeader>
							<CardContent className="space-y-6">
								{indError && (
									<div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">{indError}</div>
								)}
								<form onSubmit={handleIndividualSubmit} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="ind-email" className="text-sm font-medium">Email Address</Label>
										<Input id="ind-email" type="email" value={indEmail} onChange={e => setIndEmail(e.target.value)} placeholder="your.email@example.com" className="h-11" required />
									</div>
									<div className="space-y-2">
										<Label htmlFor="ind-password" className="text-sm font-medium">Password</Label>
										<div className="relative">
											<Input id="ind-password" type={indShowPassword ? 'text' : 'password'} value={indPassword} onChange={e => setIndPassword(e.target.value)} placeholder="Enter your password" className="h-11 pr-10" required />
											<Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setIndShowPassword(!indShowPassword)}>
												{indShowPassword ? <EyeOff className="size-4 text-muted-foreground" /> : <Eye className="size-4 text-muted-foreground" />}
											</Button>
										</div>
									</div>
									<Button type="submit" className="w-full h-11 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium" disabled={indLoading}>
										{indLoading ? 'Signing In...' : 'Begin Your Journey'}
									</Button>
                </form>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    New to KEM Discovery?{' '}
                    <Link href="/individual/signup" className="text-purple-600 hover:text-purple-500 font-medium underline underline-offset-4">
                      Create Your Account
                    </Link>
                  </p>
                </div>
							</CardContent>
						</Card>

						{/* Corporate Portal */}
						<Card className="border-0 shadow-xl bg-gradient-to-br from-white to-blue-50/30 dark:from-gray-900 dark:to-blue-950/30">
							<CardHeader className="text-center pb-4">
								<div className="mx-auto size-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center shadow-lg mb-4">
									<Building2 className="size-8" />
								</div>
								<CardTitle className="text-2xl font-bold">Corporate Portal</CardTitle>
								<p className="text-sm text-muted-foreground mt-2">Access your organizational dashboard with enhanced team insights</p>
							</CardHeader>
							<CardContent className="space-y-6">
								{corpError && (
									<div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">{corpError}</div>
								)}
								{corpMessage && (
									<div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800/20 text-green-800 dark:text-green-200 text-sm">{corpMessage}</div>
								)}
								
								{/* SSO Section */}
								<div className="space-y-4">
									<div className="text-center">
										<h3 className="text-lg font-semibold mb-2">Sign in with SSO</h3>
										<p className="text-sm text-muted-foreground mb-4">
											Enter your corporate email to find your organization
										</p>
									</div>

									<div className="space-y-4">
										<div className="space-y-2">
											<Label htmlFor="corp-email" className="text-sm font-medium">Corporate Email</Label>
											<Input 
												id="corp-email" 
												type="email" 
												value={corpEmail} 
												onChange={handleCorporateEmailChange} 
												placeholder="you@company.com" 
												className="h-11" 
												required 
											/>
										</div>

										<Button 
											onClick={handleCorporateSearch}
											disabled={!corpEmail}
											className="w-full h-11 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium"
										>
											Search for Organization
										</Button>

										{/* Organization Selection Area - Fixed Height */}
										<div className="min-h-[200px] w-full">
											{isSearching && !selectedOrganization ? (
												<WorkOSOrganizationSelector
													email={corpEmail}
													onOrganizationSelect={setSelectedOrganization}
												/>
											) : selectedOrganization && selectedOrganization !== 'new' ? (
												<div className="space-y-3">
													<div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
														<p className="text-sm text-blue-800 dark:text-blue-200">
															Ready to sign in with your organization
														</p>
													</div>
													<WorkOSSSOButton
														organizationId={selectedOrganization}
														variant="default"
														size="default"
													/>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => setSelectedOrganization(null)}
														className="w-full"
													>
														Choose different organization
													</Button>
												</div>
											) : selectedOrganization === 'new' ? (
												<div className="p-4 border rounded-lg text-center">
													<p className="text-sm text-muted-foreground mb-3">
														Contact your administrator to set up SSO for your organization.
													</p>
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={() => setSelectedOrganization(null)}
														className="w-full"
													>
														Back to organization selection
													</Button>
												</div>
											) : null}
										</div>
									</div>
								</div>

								<div className="text-center pt-4 border-t">
									<p className="text-sm text-muted-foreground">
										Want to set up corporate access?{' '}
										<Link href="/corporate" className="text-blue-600 hover:text-blue-500 font-medium underline underline-offset-4">
											Get Started
										</Link>
									</p>
								</div>
							</CardContent>
						</Card>
					</div>
          {/* Below-card tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Sparkles className="size-3" />
                <span>Personalized Results</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="size-3" />
                <span>100% Private</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Shield className="size-3" />
                <span>Enterprise Security</span>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="size-3" />
                <span>SSO Only</span>
              </div>
            </div>
          </div>
				</div>
			</div>
		</>
	)
}


